import { supabase } from './supabase'
import { api } from './api'
import { ENV } from './env'
import {
  assertionJSON,
  creationOptions,
  passkeyHostOk,
  promptTimeoutMs,
  registrationJSON,
  requestOptions,
  type CreationJSON,
  type PasskeyEnv,
  type RequestJSON,
} from './passkey-rules'

export { challengeFresh, passkeyProblem, type PasskeyEnv, type PasskeyProblem } from './passkey-rules'

/**
 * Passkeys as RepTree's second sign-in step. Hosted Supabase has no WebAuthn second step, so RepTree's own server
 * checks the passkey (server lib/passkeys) and then passes the account's hidden bridge factor for this session:
 * Supabase lifts it to aal2 exactly as for an authenticator-app code, and the server hands the lifted session back
 * here to adopt. This is NOT Supabase passkey sign-in (signInWithPasskey / auth.passkey), which nothing here touches.
 *
 * The challenge is fetched ahead (prepare*), and the button's click handler calls navigator.credentials directly
 * (startCeremony) with nothing awaited first, which keeps Safari's user activation; finish* then posts the answer.
 */

/** Whether a passkey ceremony can run in this browser on this host, answered at once (for copy and routing). */
export const passkeyUsableHere = (): boolean =>
  typeof window !== 'undefined' &&
  window.isSecureContext &&
  typeof window.PublicKeyCredential === 'function' &&
  passkeyHostOk(window.location.hostname, ENV.PASSKEY_RP_ID)

/** What this browser, on this host, can do. */
export async function passkeyEnv(): Promise<PasskeyEnv> {
  const usable = passkeyUsableHere()
  if (!usable) return { usable: false, platform: false }
  let platform = false
  try {
    platform = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    platform = false
  }
  return { usable, platform }
}

/** A challenge fetched ahead of the tap, ready for startCeremony. */
export type Prepared = {
  challengeId: string
  /** When the challenge arrived, by this device's clock (freshness is judged from this, see challengeFresh). */
  fetchedAt: number
} & ({ kind: 'create'; publicKey: CreationJSON } | { kind: 'request'; publicKey: RequestJSON })

/** A passkey as its owner sees it (server lib/passkeys/store.ts PasskeySummary). */
export interface PasskeySummary {
  id: string
  name: string
  created_at: string
  last_used_at: string | null
  backed_up: boolean
  backup_eligible: boolean
}

/** The account's second steps (works before this session has passed one): its passkeys, its authenticator apps, and
 *  `stuck` when it has a second step that neither can pass (only support can help). */
export interface SecondSteps {
  passkeys: PasskeySummary[]
  apps: { id: string; name: string; created_at: string | null }[]
  stuck: boolean
}
export const secondSteps = (): Promise<SecondSteps> => api<SecondSteps>('/me/second-step')

/** Get a new passkey ready to create. */
export async function prepareRegistration(): Promise<Prepared> {
  const r = await api<{ challengeId: string; publicKey: CreationJSON }>('/me/passkeys/options', { method: 'POST', body: '{}' })
  return { kind: 'create', challengeId: r.challengeId, publicKey: r.publicKey, fetchedAt: Date.now() }
}

/** Get a confirmation ready: any of the account's passkeys, or just `passkeyId` (confirming with one that stays). */
export async function prepareConfirmation(passkeyId?: string): Promise<Prepared> {
  const r = await api<{ challengeId: string; publicKey: RequestJSON }>('/me/passkeys/confirm/options', {
    method: 'POST',
    body: JSON.stringify(passkeyId ? { passkeyId } : {}),
  })
  return { kind: 'request', challengeId: r.challengeId, publicKey: r.publicKey, fetchedAt: Date.now() }
}

/**
 * The browser prompt (Face ID, Touch ID, Windows Hello, screen lock, or a phone). Call it straight from the click
 * handler, with nothing awaited before it, so Safari still counts the tap as the reason for the prompt.
 */
export function startCeremony(p: Prepared, opts: { platform: boolean; email: string; signal?: AbortSignal }): Promise<Credential | null> {
  const timeoutMs = promptTimeoutMs(p.fetchedAt, Date.now())
  if (p.kind === 'create') return navigator.credentials.create({ publicKey: creationOptions(p.publicKey, { ...opts, timeoutMs }), signal: opts.signal })
  return navigator.credentials.get({ publicKey: requestOptions(p.publicKey, timeoutMs), signal: opts.signal })
}

type Tokens = { access_token: string; refresh_token: string }

/** Take on the session the server lifted to aal2 (the SDK stores it and tells the app). */
async function adopt(session: Tokens): Promise<void> {
  const { error } = await supabase.auth.setSession({ access_token: session.access_token, refresh_token: session.refresh_token })
  if (error) throw error
}

/**
 * Around a call that may lift this session: the server's second step gives the session a new refresh token, so the
 * SDK's own timer must not refresh with the old one in between.
 */
async function autoRefreshPaused<T>(call: () => Promise<T>): Promise<T> {
  await supabase.auth.stopAutoRefresh()
  try {
    return await call()
  } finally {
    await supabase.auth.startAutoRefresh()
  }
}

/** A failure that says nothing about whether the server's work went through (a dropped connection, a server error). */
const answerLost = (e: unknown): boolean => {
  const status = (e as { status?: number } | null)?.status
  return status === undefined || status >= 500
}

const cancelled = () => Object.assign(new Error('no credential'), { name: 'NotAllowedError' })

/**
 * Send a new passkey to the server. The account's first one also lifts this session to aal2: the server hands the
 * lifted session back to adopt, or asks for a refresh when Supabase's answer to it was lost on the way. If the server's
 * own answer is lost, a refresh picks up a lift that may have happened, and the failure still stands (the card then
 * reloads what the account really has).
 */
export async function finishRegistration(p: Prepared, credential: Credential | null, device: string): Promise<PasskeySummary> {
  if (!credential) throw cancelled()
  return autoRefreshPaused(async () => {
    let r: { passkey: PasskeySummary; session: Tokens | null; refresh: boolean }
    try {
      r = await api('/me/passkeys', {
        method: 'POST',
        body: JSON.stringify({ challengeId: p.challengeId, credential: registrationJSON(credential as unknown as Parameters<typeof registrationJSON>[0]), device }),
      })
    } catch (e) {
      if (answerLost(e)) await supabase.auth.refreshSession().catch(() => undefined)
      throw e
    }
    if (r.session) await adopt(r.session)
    else if (r.refresh) await supabase.auth.refreshSession()
    announcePasskeysChanged()
    return r.passkey
  })
}

/**
 * Send a confirmation to the server, and adopt the session it lifted to aal2. If the answer is lost on the way, one
 * refresh tells: a session that comes back aal2 was lifted (this confirmation went through).
 */
export async function finishConfirmation(p: Prepared, credential: Credential | null): Promise<void> {
  if (!credential) throw cancelled()
  return autoRefreshPaused(async () => {
    try {
      const r = await api<{ session: Tokens }>('/me/passkeys/confirm', {
        method: 'POST',
        body: JSON.stringify({ challengeId: p.challengeId, credential: assertionJSON(credential as unknown as Parameters<typeof assertionJSON>[0]) }),
      })
      await adopt(r.session)
    } catch (e) {
      if (answerLost(e)) {
        const { data } = await supabase.auth.refreshSession().catch(() => ({ data: { session: null } }))
        if (data.session && (await supabase.auth.mfa.getAuthenticatorAssuranceLevel()).data?.currentLevel === 'aal2') return
      }
      throw e
    }
  })
}

/** Remove a passkey. The account's last one turns the passkey step off, so the session is refreshed to match. */
export async function removePasskey(id: string): Promise<void> {
  const r = await api<{ lastOne: boolean }>(`/me/passkeys/${id}`, { method: 'DELETE' })
  if (r.lastOne) await supabase.auth.refreshSession()
  announcePasskeysChanged()
}

/**
 * Other tabs on this device are told when a passkey is added or removed, so a setup panel open there fetches a fresh
 * challenge: the old one would not exclude the new passkey, and the device could save a second one over it.
 */
const CHANNEL = 'reptree-passkeys'
function announcePasskeysChanged(): void {
  try {
    const c = new BroadcastChannel(CHANNEL)
    c.postMessage('changed')
    c.close()
  } catch {
    /* no BroadcastChannel here */
  }
}
export function onPasskeysChanged(fn: () => void): () => void {
  try {
    const c = new BroadcastChannel(CHANNEL)
    c.onmessage = () => fn()
    return () => c.close()
  } catch {
    return () => undefined
  }
}
